import { parseStartParam } from '@qm/shared';
import { Suspense, lazy, useEffect, useRef, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { BottomNav } from './components/layout';
import { Logo } from './components/Icon';
import { EmptyState, Spinner } from './components/ui';
import { config } from './config';
import { useAuth } from './lib/auth';
import { FavoritesPage } from './pages/Favorites';
import { HomePage } from './pages/Home';
import { ListingPage } from './pages/Listing';
import { ConsentPage } from './pages/Consent';
import { OnboardingPage } from './pages/Onboarding';
import { BlockedPage, FatalScreen } from './pages/System';
import { SearchPage } from './pages/Search';

/*
 * Редко открываемые при первом запуске экраны грузятся отдельными чанками: мастер публикации (формы, загрузка фото),
 * обращения, профиль и справка. Главная, поиск, карточка и избранное — в основном бандле (точки входа по ссылкам).
 */
const named = <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  lazy(() => load().then((m) => ({ default: m[name] })));
const SellPage = named(() => import('./pages/Sell'), 'SellPage');
const ChatsPage = named(() => import('./pages/Chats'), 'ChatsPage');
const ChatThreadPage = named(() => import('./pages/Chats'), 'ChatThreadPage');
const NotificationsPage = named(() => import('./pages/Chats'), 'NotificationsPage');
const ProfilePage = named(() => import('./pages/Profile'), 'ProfilePage');
const MyListingsPage = named(() => import('./pages/Profile'), 'MyListingsPage');
const ContactsSettingsPage = named(() => import('./pages/Profile'), 'ContactsSettingsPage');
const SettingsPage = named(() => import('./pages/Profile'), 'SettingsPage');
const BusinessProfilePage = named(() => import('./pages/Profile'), 'BusinessProfilePage');
const SellerPage = named(() => import('./pages/Seller'), 'SellerPage');
const CategoriesPage = named(() => import('./pages/Categories'), 'CategoriesPage');
const HelpPage = named(() => import('./pages/Profile'), 'HelpPage');

/** Экраны без нижней навигации (мастер публикации, карточка объявления для покупателя). */
const NO_NAV = [/^\/sell\/.+/, /^\/listing\//, /^\/chats\/.+/];

export function App() {
  const { t } = useTranslation();
  const { state, retry } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handledStart = useRef(false);

  // Deep link: t.me/<bot>/<app>?startapp=l_42 → карточка объявления.
  useEffect(() => {
    if (state.status !== 'ready' || handledStart.current) return;
    handledStart.current = true;
    const target = parseStartParam(state.startParam);
    if (target.type === 'listing' && location.pathname === '/') navigate(`/listing/${target.listingId}`);
    if (target.type === 'chat' && location.pathname === '/') navigate(`/chats/${target.conversationId}`);
  }, [state, navigate, location.pathname]);

  if (state.status === 'loading') {
    return (
      <div className="splash">
        <Logo size={64} />
        <Spinner label={config.appName} />
      </div>
    );
  }
  if (state.status === 'outside_telegram') {
    return (
      <div className="splash">
        <Logo size={64} />
        <EmptyState icon="send" title={t('auth.openInTelegram')} text={t('auth.openInTelegramText')} />
        {config.botUsername ? (
          <a className="btn btn-primary btn-md" href={`https://t.me/${config.botUsername}`}>
            @{config.botUsername}
          </a>
        ) : null}
      </div>
    );
  }
  if (state.status === 'error') return <FatalScreen error={state.error} onRetry={retry} />;
  if (state.me.status === 'blocked') return <BlockedPage />;
  if (!state.me.settings.onboardingCompleted) return <OnboardingPage />;
  if (state.me.consent.needed) return <ConsentPage />;

  const hideNav = NO_NAV.some((re) => re.test(location.pathname));
  return (
    <>
      <Suspense fallback={<div className="route-loading"><Spinner /></div>}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/listing/:id" element={<ListingPage />} />
        <Route path="/favorites" element={<FavoritesPage />} />
        <Route path="/sell" element={<SellPage />} />
        <Route path="/sell/:id" element={<SellPage />} />
        <Route path="/chats" element={<ChatsPage />} />
        <Route path="/chats/:id" element={<ChatThreadPage />} />
        {/* старый адрес раздела «Обращения» */}
        <Route path="/messages" element={<Navigate to="/chats?tab=inquiries" replace />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/profile/listings" element={<MyListingsPage />} />
        <Route path="/profile/contacts" element={<ContactsSettingsPage />} />
        <Route path="/profile/settings" element={<SettingsPage />} />
        <Route path="/profile/business" element={<BusinessProfilePage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/seller/:id" element={<SellerPage />} />
        <Route path="/categories" element={<CategoriesPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      {hideNav ? null : <BottomNav />}
    </>
  );
}
